import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMetricsSummary, useLiveStats, useExportData, useAdoptionMetrics } from '../hooks/useMetrics';
import AdoptionMetrics from './AdoptionMetrics';
import LaunchControl from './LaunchControl';
import AuditLogViewer from './AuditLogViewer';
import UserManagementTable from './UserManagementTable';
import BetaWaitlist from './BetaWaitlist'; // BETA MODE

const MetricsCards: React.FC = () => {
  const { user } = useAuth();
  const [timeframe, setTimeframe] = useState<'day' | 'week' | 'month'>('week');
  const [activeTab, setActiveTab] = useState<'platform' | 'adoption' | 'launch' | 'audit' | 'users' | 'beta'>('platform'); // BETA MODE: Added 'beta' tab

  // Check if user is admin by role (not email domain)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // React Query hooks
  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
    isRefetching: metricsRefetching
  } = useMetricsSummary(timeframe);

  const {
    data: liveStats,
    isLoading: liveStatsLoading,
    error: liveStatsError
  } = useLiveStats();

  const {
    data: adoptionData,
    isLoading: adoptionLoading
  } = useAdoptionMetrics();

  const { exportMetrics } = useExportData();

  // Determine overall loading and error states
  const loading = metricsLoading || liveStatsLoading;
  const error = metricsError || liveStatsError;

  // Don't show metrics for non-admin users
  if (!isAdmin) return null;

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    return (
      <div className="mb-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Unable to load business metrics
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {errorMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !metrics && !liveStats) {
    return (
      <div className="mb-8">
        <div className="animate-pulse">
          <div className="bg-gray-200 dark:bg-gray-700 h-20 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 dark:bg-gray-700 h-24 rounded-xl"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-gray-200 dark:bg-gray-700 h-48 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0;
    return {
      text: `${isPositive ? '+' : ''}${growth}%`,
      color: isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
      icon: isPositive ? '↗' : '↘'
    };
  };

  const StatCard: React.FC<{
    label: string;
    value: string | number;
    icon: string;
    color: string;
    growth?: number;
    subtitle?: string;
  }> = ({ label, value, icon, color, growth, subtitle }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center text-xl`}>
          {icon}
        </div>
        {growth !== undefined && (
          <span className={`text-xs font-semibold ${formatGrowth(growth).color} flex items-center gap-1`}>
            {formatGrowth(growth).icon} {formatGrowth(growth).text}
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mb-0.5">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
          {label}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="mb-8 space-y-6">
      {/* Navigation Tabs with Export Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-2 border border-gray-200 dark:border-gray-700 flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto overflow-x-auto">
          <button
            onClick={() => setActiveTab('platform')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'platform'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Platform Overview
          </button>
          <button
            onClick={() => setActiveTab('adoption')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'adoption'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Adoption Metrics
          </button>
          <button
            onClick={() => setActiveTab('launch')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'launch'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Launch Control
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'audit'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'users'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Users
          </button>
          {/* BETA MODE: Beta Waitlist tab */}
          <button
            onClick={() => setActiveTab('beta')}
            className={`px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'beta'
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Beta Waitlist
          </button>
        </div>

        {/* Export CSV Button - Only show on Platform Overview and Adoption Metrics */}
        {(activeTab === 'platform' || activeTab === 'adoption') && (
          <button
            onClick={async () => {
              try {
                await exportMetrics(timeframe, 'csv', activeTab === 'adoption');
              } catch (error) {
                console.error('Export failed:', error);
              }
            }}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {/* Launch Control Tab */}
      {activeTab === 'launch' && (
        <LaunchControl />
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <AuditLogViewer />
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <UserManagementTable />
      )}

      {/* BETA MODE: Beta Waitlist Tab */}
      {activeTab === 'beta' && (
        <BetaWaitlist />
      )}

      {/* Adoption Metrics Tab */}
      {activeTab === 'adoption' && (
        <AdoptionMetrics data={adoptionData} isLoading={adoptionLoading} />
      )}

      {/* Platform Overview Tab */}
      {activeTab === 'platform' && (
        <>
          {/* Top Bar: Live Stats + Timeframe Selector */}
          {liveStats && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-gray-900 dark:text-white text-xs sm:text-sm font-semibold">LIVE DASHBOARD</span>
                    {(metricsRefetching || liveStatsLoading) && (
                      <svg className="w-4 h-4 text-primary-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 sm:px-3 py-1.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm"><strong>{liveStats.activeVisitors}</strong> Active</span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 sm:px-3 py-1.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm"><strong>{liveStats.currentResponseTime}ms</strong> Response</span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 sm:px-3 py-1.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm"><strong>{liveStats.systemUptime}h</strong> Uptime</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {(['day', 'week', 'month'] as const).map((period) => (
                    <button
                      key={period}
                      onClick={() => setTimeframe(period)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                        timeframe === period
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {period === 'day' ? 'Today' : period === 'week' ? 'Week' : 'Month'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Platform KPIs */}
          {metrics && (
            <>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  Platform Overview
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    {timeframe === 'day' ? 'Last 24 hours' : timeframe === 'week' ? 'Last 7 days' : 'Last 30 days'}
                  </span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <StatCard
                    label="Chat Sessions"
                    value={metrics.kpis.chatSessions.current}
                    icon="💬"
                    color="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                    growth={metrics.kpis.chatSessions.growth}
                  />

                  <StatCard
                    label="Bookings"
                    value={metrics.kpis.bookings.current}
                    icon="📅"
                    color="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                    growth={metrics.kpis.bookings.growth}
                  />

                  <StatCard
                    label="Conversion Rate"
                    value={`${metrics.kpis.conversionRate.current}%`}
                    icon="🎯"
                    color="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                  />
                </div>
              </div>

              {/* Section 2: Revenue Metrics */}
              {metrics.revenue && (
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                Revenue & Growth
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <StatCard
                  label="Monthly Recurring Revenue"
                  value={`$${metrics.revenue.mrr.toLocaleString()}`}
                  icon="💵"
                  color="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                  subtitle={`$${metrics.revenue.arr.toLocaleString()} ARR`}
                />

                <StatCard
                  label="Paying Customers"
                  value={metrics.revenue.payingUsers}
                  icon="⭐"
                  color="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                  subtitle={`${metrics.revenue.trialUsers} on trial`}
                />

                <StatCard
                  label="Total Users"
                  value={metrics.revenue.totalUsers}
                  icon="👤"
                  color="bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400"
                  growth={metrics.revenue.signupGrowth}
                  subtitle={`${metrics.revenue.newSignups} new this period`}
                />

                <StatCard
                  label="Churn Rate"
                  value={`${metrics.revenue.churnRate}%`}
                  icon="📉"
                  color="bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
                  subtitle={`${metrics.revenue.trialConversionRate}% trial conversion`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Revenue per User"
                  value={`$${metrics.revenue.arpu.toFixed(2)}`}
                  icon="💎"
                  color="bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400"
                  subtitle="Average ARPU"
                />

                <StatCard
                  label="Total Agents"
                  value={metrics.revenue.totalAgents}
                  icon="🤖"
                  color="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400"
                  subtitle={`${metrics.revenue.avgAgentsPerUser.toFixed(1)} per user avg`}
                />

                <StatCard
                  label="Annual Run Rate"
                  value={`$${(metrics.revenue.arr / 1000).toFixed(1)}K`}
                  icon="📈"
                  color="bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400"
                  subtitle={`${metrics.revenue.payingUsers} × $250/mo × 12`}
                />
              </div>
            </div>
          )}

          {/* Section 3: Charts Side-by-Side */}
          {metrics.charts && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Analytics & Insights
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Services Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    🏆 Top Booked Services
                  </h3>
                  {metrics.charts.topServices && metrics.charts.topServices.length > 0 ? (
                    <div className="space-y-3">
                      {metrics.charts.topServices.slice(0, 5).map((service: any, index: number) => {
                        const maxBookings = metrics.charts.topServices[0]?.bookings || 1;
                        const percentage = (service.bookings / maxBookings) * 100;
                        return (
                          <div key={index}>
                            <div className="flex justify-between items-center mb-1.5">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                                {service.service || 'Unknown'}
                              </span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white ml-2">
                                {service.bookings}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div
                                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2.5 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm">No booking data yet</p>
                    </div>
                  )}
                </div>

                {/* Traffic Sources */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    🌐 Traffic Sources
                  </h3>
                  {metrics.charts.trafficSources && metrics.charts.trafficSources.length > 0 ? (
                    <div className="space-y-3">
                      {metrics.charts.trafficSources.slice(0, 5).map((source: any, index: number) => {
                        const total = metrics.charts.trafficSources.reduce((sum: number, s: any) => sum + s.count, 0);
                        const percentage = total > 0 ? Math.round((source.count / total) * 100) : 0;
                        const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500'];
                        return (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`}></div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {source.source || 'Direct'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {source.count}
                              </span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white w-12 text-right">
                                {percentage}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm">No traffic data yet</p>
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MetricsCards;
